import {FlexColumn, Text} from '@forecast-it/design-system';
import React from 'react';
import {useIntl} from 'react-intl';
import type {RunOutcome} from './novaInvocationStore';

export interface FailurePresentationProps {
	outcome: RunOutcome;
	// When the run was refused, as the saved record states it. Only the out-of-credits message reads
	// it, to name the reset that applied to that run; absent, the reset is dated from now.
	ranAt?: string | null;
}

// The AI credit pool resets on the 1st of each calendar month in UTC, whatever the account's billing
// date. graphql masks the gateway's own refusal text, so the date is not in the response - this is
// the documented rule it would have stated. Dated from the run rather than today, so a record read
// back in a later month names the reset that applied to it.
export function creditResetDateFor(ranAt?: string | null): Date {
	const parsed = ranAt ? new Date(ranAt) : null;
	const from = parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
	return new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 1));
}

const CAUSE_MESSAGE_ID: Record<string, string> = {
	missing_access: 'nova_invocation.failure.cause.missing_access',
	timeout: 'nova_invocation.failure.cause.timeout',
	unavailable_model: 'nova_invocation.failure.cause.unavailable_model',
	permission: 'nova_invocation.failure.cause.permission',
	document_processing: 'nova_invocation.failure.cause.document_processing',
	at_capacity: 'nova_invocation.failure.cause.at_capacity',
};

// Self-contained and prop-driven, matching RoutingDisclosure/BasedOnList — no host renders this
// yet, since presenting a run's outcome belongs inside a conversation view that does not exist
// in this codebase yet. Built and tested standalone until one does.
export const FailurePresentation = (props: FailurePresentationProps): JSX.Element | null => {
	const {formatMessage, formatDate} = useIntl();
	const {outcome} = props;

	if (outcome.kind === 'succeeded') return null;

	// Not a failure, and not offered as one: no "couldn't finish", and no invitation to try again
	// that cannot work until the pool resets. Worded as chat words the same refusal, so one pool is
	// not described two ways to the same customer.
	if (outcome.kind === 'failed' && outcome.cause === 'out_of_credits') {
		const resetDate = formatDate(creditResetDateFor(props.ranAt), {
			day: 'numeric',
			month: 'long',
			year: 'numeric',
			timeZone: 'UTC',
		});
		return (
			<FlexColumn data-cy="nova-out-of-credits">
				<Text>{formatMessage({id: 'nova_invocation.out_of_credits'}, {resetDate})}</Text>
			</FlexColumn>
		);
	}

	if (outcome.kind === 'cancelled') {
		return (
			<FlexColumn data-cy="nova-cancelled">
				<Text>{formatMessage({id: 'nova_invocation.cancelled.title'})}</Text>
				<Text>{formatMessage({id: 'nova_invocation.cancelled.nothing_posted'})}</Text>
			</FlexColumn>
		);
	}

	const causeMessageId = CAUSE_MESSAGE_ID[outcome.cause] ?? 'nova_invocation.failure.cause.generic';
	return (
		<FlexColumn data-cy="nova-failure">
			<Text>{formatMessage({id: 'nova_invocation.failure.title'})}</Text>
			<Text>{formatMessage({id: causeMessageId})}</Text>
			<Text>{formatMessage({id: 'nova_invocation.failure.nothing_posted'})}</Text>
			<Text>{formatMessage({id: 'nova_invocation.failure.invoke_again'})}</Text>
		</FlexColumn>
	);
};
